import { Chart } from "chart.js/auto";
import React, { useCallback, useRef, useState } from "react";
import Select from "react-select";

import {
  CustomDropdownWithChevron,
  CustomMenuWithSearch,
} from "../../../components";
import { customSelectStyles } from "../../../shared/Constants";
import { toTitleCase } from "../../../shared/Functions";

interface ChartControlsProps {
  selectedColumn: string;
  dataColumns: string[];
  chartRef: React.RefObject<Chart | null>;
  onColumnChange: (column: string) => void;
}

const navigationButtonClasses =
  "w-[38px] h-[38px] border border-gray-300 bg-white text-gray-700 rounded-md cursor-pointer transition-all duration-200 hover:bg-gray-50 shadow-sm text-sm leading-5 font-normal";

export const ChartControls: React.FC<ChartControlsProps> = ({
  selectedColumn,
  dataColumns,
  chartRef,
  onColumnChange,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const selectContainerRef = useRef<HTMLDivElement>(null);

  // Filter options based on search input
  const filterOption = (option: any, searchInput: string) => {
    if (!searchInput) return true;
    const search = searchInput.toLowerCase();
    return option.data.label.toLowerCase().includes(search);
  };

  const handleMenuClose = useCallback(() => {
    // Only close if the click was outside the container
    setTimeout(() => {
      if (
        selectContainerRef.current &&
        !selectContainerRef.current.contains(document.activeElement)
      ) {
        setMenuIsOpen(false);
        setInputValue("");
      }
    }, 0);
  }, []);

  const validateChartScales = useCallback((): boolean => {
    return !!(
      chartRef.current?.scales?.y && chartRef.current.options.scales?.y
    );
  }, [chartRef]);

  const calculatePanAmount = (
    yRange: number,
    direction: "up" | "down",
  ): number => {
    const multiplier = direction === "up" ? 1 : -1;
    return yRange * 0.1 * multiplier;
  };

  const updateYAxisOptions = (
    yOptions: any,
    yScale: any,
    panAmount: number,
  ): void => {
    if (yOptions.min !== undefined && yOptions.max !== undefined) {
      yOptions.min = (yOptions.min as number) + panAmount;
      yOptions.max = (yOptions.max as number) + panAmount;
    } else {
      yOptions.min = yScale.min + panAmount;
      yOptions.max = yScale.max + panAmount;
    }
  };

  const panYAxis = useCallback(
    (direction: "up" | "down") => {
      if (!validateChartScales()) return;

      const yScale = chartRef.current!.scales.y;
      const yRange = yScale.max - yScale.min;
      const panAmount = calculatePanAmount(yRange, direction);
      const yOptions = chartRef.current!.options.scales!.y;

      updateYAxisOptions(yOptions, yScale, panAmount);
      chartRef.current!.update();
    },
    [chartRef, validateChartScales],
  );

  const calculateXPanAmount = (
    xRange: number,
    direction: "left" | "right",
  ): number => {
    const multiplier = direction === "right" ? 1 : -1;
    return xRange * 0.1 * multiplier;
  };

  const updateXAxisOptions = (
    xOptions: any,
    xScale: any,
    panAmount: number,
  ): void => {
    if (xOptions.min !== undefined && xOptions.max !== undefined) {
      xOptions.min = (xOptions.min as number) + panAmount;
      xOptions.max = (xOptions.max as number) + panAmount;
    } else {
      xOptions.min = xScale.min + panAmount;
      xOptions.max = xScale.max + panAmount;
    }
  };

  const panXAxis = useCallback(
    (direction: "left" | "right") => {
      if (!chartRef.current?.scales?.x || !chartRef.current.options.scales?.x)
        return;

      const xScale = chartRef.current.scales.x;
      const xRange = xScale.max - xScale.min;
      const panAmount = calculateXPanAmount(xRange, direction);
      const xOptions = chartRef.current.options.scales.x;

      updateXAxisOptions(xOptions, xScale, panAmount);
      chartRef.current.update();
    },
    [chartRef],
  );

  // Chart navigation handlers
  const handlePanUp = useCallback(() => panYAxis("up"), [panYAxis]);
  const handlePanDown = useCallback(() => panYAxis("down"), [panYAxis]);
  const handlePanLeft = useCallback(() => panXAxis("left"), [panXAxis]);
  const handlePanRight = useCallback(() => panXAxis("right"), [panXAxis]);

  const validateChartScalesForZoom = useCallback((): boolean => {
    return !!(chartRef.current?.scales?.x && chartRef.current?.scales?.y);
  }, [chartRef]);

  const calculateZoomFactor = (direction: "in" | "out"): number => {
    return direction === "in" ? 0.1 : -0.1;
  };

  const calculateScaleRanges = useCallback(() => {
    const xScale = chartRef.current!.scales.x;
    const yScale = chartRef.current!.scales.y;
    return {
      xRange: xScale.max - xScale.min,
      yRange: yScale.max - yScale.min,
      xScale,
      yScale,
    };
  }, [chartRef]);

  const updateXAxisZoom = useCallback(
    (xScale: any, xRange: number, zoomFactor: number) => {
      if (chartRef.current?.options?.scales?.x) {
        chartRef.current.options.scales.x.min =
          xScale.min + xRange * zoomFactor;
        chartRef.current.options.scales.x.max =
          xScale.max - xRange * zoomFactor;
      }
    },
    [chartRef],
  );

  const updateYAxisZoom = useCallback(
    (yScale: any, yRange: number, zoomFactor: number) => {
      if (chartRef.current?.options?.scales?.y) {
        chartRef.current.options.scales.y.min =
          yScale.min + yRange * zoomFactor;
        chartRef.current.options.scales.y.max =
          yScale.max - yRange * zoomFactor;
      }
    },
    [chartRef],
  );

  const zoomChart = useCallback(
    (direction: "in" | "out") => {
      if (!validateChartScalesForZoom()) return;

      const { xRange, yRange, xScale, yScale } = calculateScaleRanges();
      const zoomFactor = calculateZoomFactor(direction);

      updateXAxisZoom(xScale, xRange, zoomFactor);
      updateYAxisZoom(yScale, yRange, zoomFactor);

      chartRef.current!.update();
    },
    [
      chartRef,
      validateChartScalesForZoom,
      calculateScaleRanges,
      updateXAxisZoom,
      updateYAxisZoom,
    ],
  );

  const handleZoomIn = useCallback(() => zoomChart("in"), [zoomChart]);
  const handleZoomOut = useCallback(() => zoomChart("out"), [zoomChart]);

  const handleResetZoom = useCallback(() => {
    if (
      chartRef.current?.options?.scales?.x &&
      chartRef.current?.options?.scales?.y
    ) {
      delete chartRef.current.options.scales.x.min;
      delete chartRef.current.options.scales.x.max;
      delete chartRef.current.options.scales.y.min;
      delete chartRef.current.options.scales.y.max;
      chartRef.current.update();
    }
  }, [chartRef]);

  return (
    <div
      className="mb-1 ml-2 flex-shrink-0 flex items-center justify-between"
      data-testid="chart-controls"
    >
      <div ref={selectContainerRef}>
        <Select
          value={{
            value: selectedColumn,
            label: selectedColumn
              ? toTitleCase(selectedColumn.replace(/_/g, " "))
              : "Select Chart Column",
          }}
          onChange={(option) => {
            if (option && option.value !== undefined) {
              onColumnChange(option.value);
              setMenuIsOpen(false);
              setInputValue("");
            }
          }}
          options={dataColumns.map((col) => ({
            value: col,
            label: toTitleCase(col.replace(/_/g, " ")),
          }))}
          filterOption={filterOption}
          inputValue={inputValue}
          onInputChange={(val) => setInputValue(val)}
          onMenuOpen={() => setMenuIsOpen(true)}
          onMenuClose={handleMenuClose}
          menuIsOpen={menuIsOpen}
          className="text-sm"
          classNamePrefix="chart-column-select"
          isSearchable={false}
          components={{
            DropdownIndicator: CustomDropdownWithChevron,
            IndicatorSeparator: () => null,
            MenuList: CustomMenuWithSearch,
          }}
          styles={{
            ...customSelectStyles,
            control: (base) => ({
              ...base,
              width: "263px",
            }),
            option: (base, state) => ({
              ...base,
              backgroundColor: state.isFocused ? "#F3F4F6" : "white",
              color: state.isSelected ? "#FB6A07" : "#374151",
              cursor: "pointer",
              padding: "8px 12px",
            }),
          }}
        />
      </div>
      <div
        className="float-right h-[38px] flex items-center justify-end gap-3 opacity-100 mr-2"
        data-testid="chart-navigation-controls"
      >
        <span
          className="text-[12px] text-gray-600 font-normal leading-[20px] tracking-[0%]"
          data-testid="chart-navigation-hint"
        >
          Use arrows to pan
        </span>

        <button
          className={navigationButtonClasses}
          data-testid="pan-left-button"
          onClick={handlePanLeft}
          title="Pan left"
        >
          ←
        </button>
        <button
          className={navigationButtonClasses}
          data-testid="pan-right-button"
          onClick={handlePanRight}
          title="Pan right"
        >
          →
        </button>
        <button
          className={navigationButtonClasses}
          data-testid="pan-up-button"
          onClick={handlePanUp}
          title="Pan up"
        >
          ↑
        </button>
        <button
          className={navigationButtonClasses}
          data-testid="pan-down-button"
          onClick={handlePanDown}
          title="Pan down"
        >
          ↓
        </button>
        <span className="text-gray-500" data-testid="navigation-separator">
          |
        </span>
        <button
          className={navigationButtonClasses}
          data-testid="zoom-in-button"
          onClick={handleZoomIn}
        >
          +
        </button>
        <button
          className={navigationButtonClasses}
          data-testid="zoom-out-button"
          onClick={handleZoomOut}
        >
          -
        </button>
        <button
          className="w-[63px] h-[38px] border border-gray-300 bg-white text-gray-700 rounded-md cursor-pointer transition-all duration-200 hover:bg-gray-50 shadow-sm text-sm leading-5 font-normal"
          data-testid="reset-button"
          onClick={handleResetZoom}
        >
          Reset
        </button>
      </div>
    </div>
  );
};
